import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2 } from "lucide-react";

const PHONE_DISPLAY = "(786) 565-2545";
const PHONE_TEL = "+17865652545";
const EMAIL = "jsalas@josengineer.com";
const ADDRESS = "16804 SW 109 CT, Miami, FL 33157";

export const Contact = ({ t }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Project inquiry from ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\n\n${form.message}`
    );
    window.location.href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;
    setSent(true);
    setTimeout(() => setSent(false), 6000);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-40 bg-[#FDFDF9] border-t border-[#183321]/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: Form */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#183321]/40" />
              <span className="text-xs uppercase tracking-[0.25em] text-[#4A6B53] font-semibold">
                {t.contact.eyebrow}
              </span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#183321] font-medium">
              {t.contact.title}
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#4A6B53] max-w-xl leading-relaxed">
              {t.contact.sub}
            </p>

            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <div className="md:col-span-1">
                <label className="text-xs uppercase tracking-widest text-[#4A6B53] font-semibold mb-2 block">
                  {t.contact.form.name}
                </label>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-[#183321]/30 py-3 text-[#183321] focus:outline-none focus:border-[#2A4D35] transition-colors"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-xs uppercase tracking-widest text-[#4A6B53] font-semibold mb-2 block">
                  {t.contact.form.email}
                </label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-[#183321]/30 py-3 text-[#183321] focus:outline-none focus:border-[#2A4D35] transition-colors"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-[#4A6B53] font-semibold mb-2 block">
                  {t.contact.form.phone}
                </label>
                <input
                  data-testid="contact-input-phone"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-[#183321]/30 py-3 text-[#183321] focus:outline-none focus:border-[#2A4D35] transition-colors"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-[#4A6B53] font-semibold mb-2 block">
                  {t.contact.form.message}
                </label>
                <textarea
                  data-testid="contact-input-message"
                  name="message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-[#183321]/30 py-3 text-[#183321] focus:outline-none focus:border-[#2A4D35] transition-colors resize-none"
                />
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-6 mt-2">
                <button
                  data-testid="contact-submit"
                  type="submit"
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#2A4D35] text-[#FDFDF9] text-sm uppercase tracking-widest font-semibold rounded-sm hover:bg-[#3C664A] hover:-translate-y-0.5 transition-all"
                >
                  {t.contact.form.submit}
                  <Send size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
                {sent && (
                  <div
                    data-testid="contact-form-sent"
                    className="flex items-center gap-2 text-sm text-[#2A4D35]"
                  >
                    <CheckCircle2 size={18} />
                    {t.contact.form.sent}
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Right: Details */}
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="bg-[#EAF1E8] border border-[#183321]/10 rounded-sm p-8 md:p-10">
              <div className="text-xs uppercase tracking-[0.25em] text-[#4A6B53] font-semibold mb-6">
                {t.contact.details_title}
              </div>

              <div className="space-y-7">
                <div data-testid="contact-address" className="flex gap-4">
                  <div className="w-10 h-10 shrink-0 rounded-sm bg-[#2A4D35] text-[#FDFDF9] flex items-center justify-center">
                    <MapPin size={16} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#4A6B53] mb-1">
                      Miami, FL
                    </div>
                    <div className="font-serif text-lg text-[#183321] leading-snug">
                      {ADDRESS}
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 shrink-0 rounded-sm bg-[#2A4D35] text-[#FDFDF9] flex items-center justify-center">
                    <Phone size={16} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#4A6B53] mb-1">
                      {t.contact.phone_label}
                    </div>
                    <a
                      data-testid="contact-phone-link"
                      href={`tel:${PHONE_TEL}`}
                      className="font-serif text-lg text-[#183321] hover:text-[#C26D5C] transition-colors"
                    >
                      {PHONE_DISPLAY}
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 shrink-0 rounded-sm bg-[#2A4D35] text-[#FDFDF9] flex items-center justify-center">
                    <Mail size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest text-[#4A6B53] mb-1">
                      {t.contact.email_label}
                    </div>
                    <a
                      data-testid="contact-email-link"
                      href={`mailto:${EMAIL}`}
                      className="font-serif text-lg text-[#183321] hover:text-[#C26D5C] transition-colors break-all"
                    >
                      {EMAIL}
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 shrink-0 rounded-sm bg-[#2A4D35] text-[#FDFDF9] flex items-center justify-center">
                    <Clock size={16} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#4A6B53] mb-1">
                      {t.contact.hours_label}
                    </div>
                    <div className="font-serif text-lg text-[#183321]">
                      {t.contact.hours_value}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10 aspect-[4/3] overflow-hidden rounded-sm border border-[#183321]/10">
                <iframe
                  data-testid="contact-map"
                  title="josengineer office location"
                  src="https://www.google.com/maps?q=16804+SW+109+CT+Miami+FL+33157&output=embed"
                  width="100%"
                  height="100%"
                  loading="lazy"
                  style={{ border: 0, filter: "saturate(0.8) contrast(0.95)" }}
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
